import type { MouseEvent, PropsWithChildren } from "react";
import { styled } from "styled-components";

const StyledToggleWrapper = styled.button<{ isOn: boolean }>`
  padding: 0;
  align-items: center;
  box-sizing: border-box;
  width: 46px;
  height: 32px;
  border-radius: 9999em;
  margin-left: auto;
  transition: all 0.3s ease;
  border: 2px solid ${({ theme, isOn }) => (isOn ? theme.colors.blue : theme.colors.inputText)};
  background-color: transparent;
  position: relative;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.buttonNeutralActiveText};
    outline-offset: 3px;
  }

  &[disabled] {
    border: 2px solid ${({ theme, isOn }) => (isOn ? `${theme.colors.blue}BB` : `${theme.colors.inputText}BB`)};
    cursor: not-allowed;

    &:after {
      opacity: 0.7;
    }
  }

  &:not([disabled]):hover {
    border: 2px solid ${({ theme, isOn }) => (isOn ? theme.colors.blueDark : theme.colors.inputTextFocused)};
  }
  &:not([disabled]):hover:after {
    background-color: ${({ theme, isOn }) => (isOn ? theme.colors.blueDark : theme.colors.inputTextFocused)};
  }
  &:after {
    content: "";
    position: absolute;
    width: 26px;
    height: 26px;
    top: 1px;
    border-radius: 13px;
    left: ${({ isOn }) => (isOn ? "15px" : "1px")};
    transition: all 0.3s ease;
    background-color: ${({ theme, isOn }) => (isOn ? theme.colors.blue : theme.colors.inputText)};
    opacity: 1;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const StyledSwitchText = styled.div`
  color: ${({ theme }) => theme.colors.inputTextFocused};
  font-weight: bold;
  display: inline-block;
`;

type UISwitchProps = PropsWithChildren & {
  className?: string;
  dataTestId?: string;
  id?: string;
  isDisabled?: boolean;
  isOn: boolean;
  onClick: (event: MouseEvent) => void;
};

export const UISwitch = ({
  children,
  className,
  dataTestId,
  id,
  isDisabled,
  isOn,
  onClick,
  ...rest
}: UISwitchProps) => (
  <StyledContainer className={className}>
    {children && <StyledSwitchText>{children}</StyledSwitchText>}
    <StyledToggleWrapper
      {...rest}
      id={id}
      isOn={isOn}
      disabled={isDisabled}
      onClick={onClick}
      data-test-id={dataTestId}
      tabIndex={0}
      role="switch"
      aria-checked={isOn}
    />
  </StyledContainer>
);
